/**
 * GA4 analytics helper — supports two transport modes.
 *
 * Spec:
 *   - MatchGo_GA4_Guideline_v1.0.md (overall design, 8 governance gates)
 *   - MatchGo_GA4_dataLayer_Spec_v1.0.2.xlsx (event/parameter contract)
 *
 * Two transport modes (pick one — they're mutually exclusive in practice):
 *
 *   A) GA4 direct via `gtag.js` (default when `REACT_APP_GA4_MEASUREMENT_ID`
 *      is set — e.g. `G-XXXXXXXXXXX`).
 *      `initAnalytics()` injects the gtag bootstrap, calls `gtag('config', id)`,
 *      and from then on every `trackEvent()` calls `gtag('event', name, params)`.
 *      No GTM container needed; data appears in GA4 within minutes.
 *
 *   B) GTM-mediated via `gtm.js` (when only `REACT_APP_GTM_CONTAINER_ID` is set).
 *      Frontend never calls `gtag()` directly; events go onto `dataLayer`
 *      and a GTM container forwards them to GA4. This matches the doc's
 *      "frontend never calls gtag" rule and is the long-term shape.
 *
 * Mode A is the fastest path to "data flowing in GA4 right now" — pick it
 * for the initial deploy. Switch to mode B later by removing
 * `REACT_APP_GA4_MEASUREMENT_ID` once a GTM container is built.
 *
 * Principles enforced regardless of mode:
 *   - Consent defaults to `denied` (set inline in `index.html` BEFORE this
 *     module loads). `grantAnalyticsConsent()` flips to `granted` once the
 *     user accepts in the consent banner.
 *   - PII guard: `email`, `phone`, `name`, etc. are stripped from any
 *     params object before send. Per §4.5/§8.5, these must NEVER reach GA.
 *   - The module is a complete no-op when neither env var is set, so dev
 *     and legal-pending environments don't ship analytics.
 */

// ---------------------------------------------------------------------------
// Window typings — minimal, just enough for our pushes / gtag.
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown> | IArguments>;
    gtag?: (...args: unknown[]) => void;
  }
}

/** Allowed user role values. `guest` is for unauthenticated visitors. */
export type UserRole = 'brand' | 'agency' | 'influencer' | 'guest';

/** Login state surfaced for analytics. */
export type LoginState = 'logged_in' | 'logged_out';

/**
 * Keys we actively scrub from any event params. Lowercase comparison.
 * Mirrors the GA guideline §4.5 PII ban list. We err on the side of
 * over-stripping — false negatives are far more painful than dropping
 * a debug field.
 */
const PII_KEYS = [
  'email',
  'phone',
  'phonenumber',
  'phone_number',
  'name',
  'fullname',
  'full_name',
  'firstname',
  'first_name',
  'lastname',
  'last_name',
  'realname',
  'real_name',
];

// ---------------------------------------------------------------------------
// Module-scope state
// ---------------------------------------------------------------------------

let initialized = false;

type TransportMode = 'ga4' | 'gtm' | null;

function getMeasurementId(): string | undefined {
  const id = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  return typeof id === 'string' && id.trim().length > 0 ? id.trim() : undefined;
}

function getGtmContainerId(): string | undefined {
  const id = process.env.REACT_APP_GTM_CONTAINER_ID;
  return typeof id === 'string' && id.trim().length > 0 ? id.trim() : undefined;
}

/**
 * Resolve which transport to use for this build. GA4 direct wins when both
 * env vars are set, because that's the simplest fastest path; the operator
 * who set both can drop the GA4 var when ready to commit to GTM-only.
 */
function getMode(): TransportMode {
  if (getMeasurementId()) return 'ga4';
  if (getGtmContainerId()) return 'gtm';
  return null;
}

function isEnabled(): boolean {
  return typeof window !== 'undefined' && getMode() !== null;
}

function getDataLayer(): Array<Record<string, unknown> | IArguments> {
  if (typeof window === 'undefined') return [];
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer as Array<Record<string, unknown> | IArguments>;
}

/**
 * Get (or create) the gtag function. gtag is just a wrapper that pushes
 * its `arguments` object onto dataLayer; we ensure that shape exists.
 */
function getGtag(): (...args: unknown[]) => void {
  if (typeof window === 'undefined') return () => {};
  if (typeof window.gtag !== 'function') {
    const dl = getDataLayer();
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dl.push(arguments as unknown as IArguments);
    };
  }
  return window.gtag;
}

// ---------------------------------------------------------------------------
// PII scrubber
// ---------------------------------------------------------------------------

function scrubPii<T extends Record<string, unknown>>(input: T): T {
  if (!input) return input;
  const out: Record<string, unknown> = {};
  let stripped = 0;
  for (const [k, v] of Object.entries(input)) {
    if (PII_KEYS.includes(k.toLowerCase())) {
      stripped += 1;
      continue;
    }
    out[k] = v;
  }
  if (stripped > 0 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      '[analytics] stripped %d PII key(s) from event params — fix the caller',
      stripped,
    );
  }
  return out as T;
}

// ---------------------------------------------------------------------------
// Bootstrap loaders — gtag.js (GA4 direct) and gtm.js (GTM mediated)
// ---------------------------------------------------------------------------

function injectScript(src: string): void {
  if (typeof document === 'undefined') return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(s, first);
  } else {
    document.head.appendChild(s);
  }
}

function bootstrapGa4Direct(measurementId: string): void {
  // Standard gtag bootstrap. The consent default in index.html already
  // ran before this; we just need to load the gtag library and call
  // `gtag('js', date)` + `gtag('config', id)`.
  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
  );
  const gtag = getGtag();
  gtag('js', new Date());
  // `send_page_view: false` because we drive page_view manually from
  // the SPA's LocationTracker — letting gtag fire it on every config
  // call would double-count.
  gtag('config', measurementId, { send_page_view: false });
}

function bootstrapGtm(containerId: string): void {
  const dl = getDataLayer();
  dl.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  injectScript(
    `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`,
  );
}

/**
 * Inject the analytics bootstrap once. Safe to call multiple times.
 * No-op when neither `REACT_APP_GA4_MEASUREMENT_ID` nor
 * `REACT_APP_GTM_CONTAINER_ID` is set.
 *
 * Call this exactly once on app boot from `index.tsx`. The consent
 * default in `index.html` must already have run.
 */
export function initAnalytics(): void {
  if (initialized) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const mode = getMode();
  if (mode === null) return;

  initialized = true;

  if (mode === 'ga4') {
    bootstrapGa4Direct(getMeasurementId()!);
  } else {
    bootstrapGtm(getGtmContainerId()!);
  }
}

/** Backwards-compatible alias. The function used to be GTM-only. */
export const initGtm = initAnalytics;

// ---------------------------------------------------------------------------
// Consent helpers
// ---------------------------------------------------------------------------

export function grantAnalyticsConsent(): void {
  if (typeof window === 'undefined') return;
  getGtag()('consent', 'update', { analytics_storage: 'granted' });
}

export function revokeAnalyticsConsent(): void {
  if (typeof window === 'undefined') return;
  getGtag()('consent', 'update', { analytics_storage: 'denied' });
}

// ---------------------------------------------------------------------------
// User context
// ---------------------------------------------------------------------------

export interface UserContext {
  user_role: UserRole;
  /** Internal opaque ID. MUST NOT be email or phone. */
  account_id?: string;
  login_state: LoginState;
}

/**
 * Push the current user context. Call when auth state changes (login /
 * logout / role known). NEVER pass email or phone in here — `account_id`
 * must be the Cognito sub or another opaque internal id.
 *
 * Mode A (GA4 direct): set as user_properties via gtag.
 * Mode B (GTM): plain dataLayer push (no `event` key, per the spec).
 */
export function pushUserContext(ctx: UserContext): void {
  if (!isEnabled()) return;
  const safe = scrubPii(ctx as unknown as Record<string, unknown>);
  if (getMode() === 'ga4') {
    const measurementId = getMeasurementId()!;
    const gtag = getGtag();
    // user_id can be set via the `config` call; user_role / account_id /
    // login_state ride along as user_properties so they're set on the
    // GA4 user record (not just on a single hit).
    gtag('config', measurementId, {
      user_id: safe.account_id || undefined,
      send_page_view: false,
    });
    gtag('set', 'user_properties', {
      user_role: safe.user_role,
      account_id: safe.account_id,
      login_state: safe.login_state,
    });
    return;
  }
  // GTM mode — plain dataLayer set per the spec README.
  getDataLayer().push(safe);
}

// ---------------------------------------------------------------------------
// Generic event push (use the typed wrappers below where possible)
// ---------------------------------------------------------------------------

export type EventParams = Record<string, string | number | boolean | undefined>;

export function trackEvent(name: string, params: EventParams = {}): void {
  if (!isEnabled()) return;
  // Drop `undefined`/`null` values so the spec rule "include only when
  // set" stays clean.
  const cleaned: EventParams = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) cleaned[k] = v;
  }
  const safe = scrubPii(cleaned);

  if (getMode() === 'ga4') {
    getGtag()('event', name, safe);
    return;
  }
  // GTM mode.
  getDataLayer().push({ event: name, ...safe });
}

// ---------------------------------------------------------------------------
// Typed event wrappers (one per row of the spec sheet)
// ---------------------------------------------------------------------------

/** §4.3, recommended event. Method = 'email' | 'kakao' | 'naver' | … */
export function trackSignUp(args: { method: string; user_role: UserRole }): void {
  trackEvent('sign_up', args);
}

/** §4.3, recommended event. */
export function trackLogin(args: { method?: string; user_role?: UserRole }): void {
  trackEvent('login', args);
}

/** Brand-funnel — campaign brief entry. */
export function trackCampaignBriefStart(args: { campaign_type?: string }): void {
  trackEvent('campaign_brief_start', args);
}

/** Brand-funnel — pre-match request submitted. */
export function trackMatchingRequest(args: {
  campaign_id: string;
  campaign_type: string;
  tier: string;
}): void {
  trackEvent('matching_request', args);
}

/** Brand-funnel — view of the proposed influencer pool. */
export function trackViewInfluencerPool(args: {
  campaign_id: string;
  pool_size?: number;
}): void {
  trackEvent('view_influencer_pool', args);
}

/** Brand-funnel — selecting a single influencer from the pool. */
export function trackSelectInfluencer(args: {
  campaign_id: string;
  influencer_id: string;
}): void {
  trackEvent('select_influencer', args);
}

/** Brand-funnel North Star event. `value` is the campaign budget in USD. */
export function trackConfirmMatch(args: {
  match_id: string;
  campaign_id: string;
  campaign_type: string;
  tier: string;
  pool_size?: number;
  selected_count?: number;
  value?: number;
  currency?: 'USD';
}): void {
  trackEvent('confirm_match', args);
}

export function trackRequestQuotation(args: {
  match_id: string;
  campaign_id: string;
}): void {
  trackEvent('request_quotation', args);
}

export function trackCampaignLaunch(args: {
  campaign_id: string;
  match_id: string;
}): void {
  trackEvent('campaign_launch', args);
}

export function trackViewReport(args: { campaign_id: string }): void {
  trackEvent('view_report', args);
}

/** Influencer-funnel — profile completion. */
export function trackCompleteProfile(args: {
  profile_completeness?: number;
}): void {
  trackEvent('complete_profile', args);
}

/** Influencer-funnel — campaign detail view. */
export function trackViewCampaign(args: {
  campaign_id: string;
  campaign_type?: string;
}): void {
  trackEvent('view_campaign', args);
}

/** Influencer-funnel macro event — campaign application submitted. */
export function trackApplyCampaign(args: { campaign_id: string }): void {
  trackEvent('apply_campaign', args);
}

/** Common — landing page consultation/lead form submit. */
export function trackGenerateLead(args: {
  lead_type?: string;
  value?: number;
}): void {
  trackEvent('generate_lead', args);
}

/** Common — main CTA clicks. `cta_location` = 'hero' | 'footer' | … */
export function trackCtaClick(args: {
  cta_text?: string;
  cta_location?: string;
}): void {
  trackEvent('cta_click', args);
}

/**
 * SPA `page_view`. We disabled the gtag auto page_view in `bootstrapGa4Direct`
 * (`send_page_view: false`) so this manual call drives every route change
 * including the initial load.
 */
export function trackPageView(args: {
  page_path: string;
  page_title?: string;
}): void {
  if (!isEnabled()) return;
  if (getMode() === 'ga4') {
    // GA4 expects `page_location` and `page_title` on a page_view event.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    getGtag()('event', 'page_view', {
      page_location: `${origin}${args.page_path}`,
      page_path: args.page_path,
      page_title: args.page_title,
    });
    return;
  }
  trackEvent('page_view', args);
}
